// CardWithModeToggle — the ONE edit door for every card on a building's Info
// tab: a plain read view, one Edit button in the header, one window holding
// everything that card can change, one Save, one Cancel.
//
// Added 2026-05-20 for the Section E full View/Edit refactor. Edit mode used to
// happen IN PLACE — the card's own rows became inputs and Save sat in the card
// header. That is why this rewrite exists (2026-09-16, info-tab-one-edit-door):
// on a phone the Account Summary card is twelve stacked fields, so a header
// Save scrolls out of reach the moment anyone starts typing. Ben chose the
// window pattern for that reason. Edit mode is now a dialog — centred on a
// desktop, a bottom sheet with a pinned footer on a phone — and the card keeps
// showing its read values behind it.
//
// Mode is still controlled by the parent so the parent can sync its `draft`
// state with the mode transitions (init on enter-edit, clear on cancel).
//
// Header chrome (the card, always in read view):
//   [icon] Title · N fields  [hide-blanks?]  [extraActions?]  [Edit]
// Window chrome (edit mode only):
//   header: Edit — Title, with the card's description as the subtitle
//   body:   the card's fields as inputs, then "Changed somewhere else"
//   footer: [Cancel] [Save]
//
// ⛔ Save callback returns `{ok, error}`; on failure the WINDOW STAYS OPEN with
// the typed values intact and the error is rendered inside it. It is never
// routed to page-level state, which would unmount the surface holding the
// evidence (TCSD-0044, PLAYBOOK §15).
//
// `children` is a render function taking the mode the BODY should display in.
// The card body always renders `children('view')`; the window renders
// `children('edit')`. One component, two surfaces, no second door.
// ============================================================================

import { ReactNode, useEffect, useState } from 'react';
import { errorMessage } from '../../../shared/errors';

export type CardMode = 'view' | 'edit';

/**
 * A fact the card SHOWS but does not OWN — the value is real and it is changed
 * by a process somewhere else. Rendered at the bottom of the edit window with
 * its current value and a button straight into that process.
 *
 * ⛔ `go` is omitted when there is no screen to go to. A fact that genuinely
 * never changes sets `never` and says so. A fact whose owning screen does not
 * exist yet sets neither and renders bare, with `note` explaining where it is
 * changed — NEVER a button to a page that will 404.
 */
export interface ElsewhereRow {
  /** The person's word for the fact — "Monthly rate", not "monthly_rate". */
  label: string;
  /** Its current value, formatted for reading. */
  value: ReactNode;
  /** The process that owns it. Omit when no screen exists today. */
  go?: { label: string; onGo: () => void };
  /** Set for a fact that is fixed for the life of the building. */
  never?: boolean;
  /** One line under the value — where it IS changed, when there is no button. */
  note?: string;
}

export interface CardWithModeToggleProps {
  title: string;
  icon?: ReactNode;
  /** Field count for the badge — "N fields" or "N / M fields". */
  populatedCount?: number;
  totalCount?: number;
  /** Controlled mode. */
  mode: CardMode;
  /** Mode setter — invoked on Edit / Cancel / Save-success. */
  onModeChange: (next: CardMode) => void;
  /**
   * Body renderer. Called with 'view' for the card and 'edit' for the window,
   * so the same field list serves both. A plain node is still accepted and is
   * rendered in the card only.
   */
  children: ReactNode | ((bodyMode: CardMode) => ReactNode);
  /** Disable Save until the parent has at least one drafted change. */
  hasChanges?: boolean;
  /** Save handler. Returns ok/error; on `ok=false` the window stays open. */
  onSave?: () => Promise<{ ok: boolean; error: string | null }>;
  /** Cancel handler — should reset the parent's draft state. */
  onCancel?: () => void;
  /** Extra header actions (e.g. "Add contact"). Visible in both modes. */
  extraActions?: ReactNode;
  /** Right-side toggle visible only in view mode (the hide-blanks toggle). */
  hideBlanksToggle?: ReactNode;
  /** Caption below the title in the card, and the subtitle in the window. */
  description?: ReactNode;
  /** Suppress the Edit/Save/Cancel chrome — used by read-only cards. */
  readOnly?: boolean;
  /**
   * Facts this card shows but does not own. Rendered at the bottom of the
   * window. A read-only card may have nothing BUT these — Financial summary is
   * exactly that — in which case the window opens on the routes alone.
   */
  elsewhere?: ElsewhereRow[];
  /**
   * Shown at the top of the window when the card has no typed fields at all,
   * so the window never opens looking broken.
   */
  emptyEditNote?: string;
}

export function CardWithModeToggle({
  title,
  icon,
  populatedCount,
  totalCount,
  mode,
  onModeChange,
  children,
  hasChanges = false,
  onSave,
  onCancel,
  extraActions,
  hideBlanksToggle,
  description,
  readOnly = false,
  elsewhere,
  emptyEditNote,
}: CardWithModeToggleProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const renderBody = (bodyMode: CardMode) =>
    typeof children === 'function' ? children(bodyMode) : bodyMode === 'view' ? children : null;

  const hasElsewhere = (elsewhere?.length ?? 0) > 0;
  // A card with no typed fields still earns an Edit button when it has routes
  // to offer — Financial summary is read-only and every number on it is
  // changed somewhere else, so its window is the list of those places.
  const canOpenWindow = !!onSave || hasElsewhere;

  async function handleSave() {
    if (!onSave) return;
    setError(null);
    setSaving(true);
    try {
      const { ok, error: errMsg } = await onSave();
      if (!ok) {
        // ⛔ The window stays open and the typed values stay in it. The person
        // has to be able to read what they wrote and copy it out.
        setError(errMsg ?? 'Save failed. Review each field and try again.');
        return;
      }
      onModeChange('view');
    } catch (e) {
      setError(errorMessage(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (onCancel) onCancel();
    setError(null);
    onModeChange('view');
  }

  function handleEnterEdit() {
    setError(null);
    onModeChange('edit');
  }

  // Escape closes the window the same way Cancel does — nothing is written.
  useEffect(() => {
    if (mode !== 'edit') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) handleCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // handleCancel is stable enough for this purpose; re-binding per render is
    // cheap and avoids a stale `saving`.
  });

  // The page behind a bottom sheet must not scroll under the person's thumb.
  useEffect(() => {
    if (mode !== 'edit') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mode]);

  const fieldsLabel =
    populatedCount != null && totalCount != null
      ? `${populatedCount} / ${totalCount} fields`
      : populatedCount != null
        ? `${populatedCount} field${populatedCount === 1 ? '' : 's'}`
        : null;

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="flex flex-wrap items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-tcs-text">{title}</h3>
        {fieldsLabel && (
          <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium text-tcs-text-muted">
            {fieldsLabel}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {hideBlanksToggle}
          {extraActions}
          {!readOnly && canOpenWindow && (
            <button
              type="button"
              onClick={handleEnterEdit}
              data-testid={`edit-${title}`}
              className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs text-tcs-text hover:bg-tcs-surface"
            >
              Edit
            </button>
          )}
        </div>
      </div>
      {description && <p className="mt-2 text-xs text-tcs-text-muted">{description}</p>}

      {/* The card is ALWAYS the read view, including while the window is open
          behind it. Nothing on this tab edits in place any more. */}
      <div className="mt-3">{renderBody('view')}</div>

      {mode === 'edit' && (
        <div
          // bg-tcs-scrim/60 is what the other 67 overlays in this app use.
          className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-5"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !saving) handleCancel();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Edit — ${title}`}
            className="flex max-h-[94vh] w-full flex-col rounded-t-2xl border border-tcs-border bg-tcs-bg shadow-2xl sm:max-h-[90vh] sm:max-w-xl sm:rounded-2xl"
          >
            <header className="border-b border-tcs-border px-5 py-4">
              <h3 className="text-base font-semibold text-tcs-text">Edit — {title}</h3>
              {description && (
                <p className="mt-1 text-xs text-tcs-text-muted">{description}</p>
              )}
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {error && (
                <div
                  role="alert"
                  className="mb-3 rounded border border-st-danger-border-strong bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong"
                >
                  {error}
                </div>
              )}
              {emptyEditNote && (
                <p className="mb-3 text-xs text-tcs-text-muted">{emptyEditNote}</p>
              )}
              {renderBody('edit')}
              {hasElsewhere && <ElsewhereSection rows={elsewhere!} />}
            </div>

            {/* Pinned. On a phone this is the whole point of the pattern: Save
                is reachable without scrolling however long the field list is. */}
            <footer className="sticky bottom-0 flex justify-end gap-2 rounded-b-none border-t border-tcs-border bg-tcs-bg px-5 py-3 sm:rounded-b-2xl">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="rounded border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
              >
                Cancel
              </button>
              {onSave && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className="rounded border border-tcs-accent bg-tcs-accent-ink px-3 py-1.5 text-xs font-medium text-tcs-accent-on hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// "Changed somewhere else" — the real facts a card shows but does not own.
//
// ⛔ A row with no `go` renders WITHOUT a button. The rule this section exists
// to serve is that the person can always see the fact and always find out where
// it is changed; a button to a screen that does not exist teaches them the app
// is broken, which is worse than no button.
// ============================================================================

function ElsewhereSection({ rows }: { rows: ElsewhereRow[] }) {
  return (
    <div className="mt-5 border-t border-tcs-border pt-4">
      <h4 className="text-xs font-semibold text-tcs-text">Changed somewhere else</h4>
      <p className="mt-0.5 text-xs text-tcs-text-muted">
        Real facts about this building that each run their own process. The button takes you
        straight there.
      </p>
      <div className="mt-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between gap-3 border-b border-tcs-border py-2 last:border-b-0"
          >
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-tcs-text">{r.label}</div>
              <div className="text-xs text-tcs-text-muted">{r.value}</div>
              {r.note && <div className="mt-0.5 text-[11px] text-tcs-text-muted">{r.note}</div>}
            </div>
            {r.go ? (
              <button
                type="button"
                onClick={r.go.onGo}
                className="whitespace-nowrap rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-accent-ink hover:border-tcs-accent"
              >
                {r.go.label} →
              </button>
            ) : r.never ? (
              <span className="whitespace-nowrap rounded border border-tcs-border bg-tcs-surface px-2.5 py-1 text-xs text-tcs-text-muted">
                Never changes
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
